import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  Collapse,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import InsightsIcon from '@mui/icons-material/Insights';
import Pagination from '@mui/material/Pagination';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';

const getActionBadge = (actionType) => {
  switch (actionType) {
    case 'requested':
      return { label: 'Submitted', color: '#7C3AED', bg: 'rgba(124, 58, 237, 0.12)' };
    case 'commented':
      return { label: 'Commented', color: '#0D9488', bg: 'rgba(13, 148, 136, 0.12)' };
    case 'opened':
      return { label: 'Opened / Viewed', color: '#0288D1', bg: 'rgba(2, 136, 209, 0.12)' };
    case 'reacted':
      return { label: 'Amen / Liked', color: '#E91E63', bg: 'rgba(233, 30, 99, 0.12)' };
    case 'prayed':
      return { label: 'Prayed & Interceded', color: '#10B981', bg: 'rgba(16, 185, 129, 0.12)' };
    case 'subscribed':
    case 'confirmed_warrior':
      return { label: 'Committed Warrior', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.12)' };
    default:
      return { label: actionType || 'Activity', color: '#6B7280', bg: 'rgba(107, 114, 128, 0.12)' };
  }
};

const ModuleAnalyticsDashboard = ({
  contentType = 'all', // 'all', 'prayer', 'devotion', 'special_prayer'
  moduleTitle = 'User Activity & Analytics',
  hideHeader = false,
  userId = null,
  selectedUser = null,
  initialSearch = '',
  onClearUser = null
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('all'); // 'day', 'week', 'month', 'year', 'all', 'custom'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState(initialSearch || '');
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' (consolidated) vs 'timeline' (chronological)
  const [chartType, setChartType] = useState('area'); // 'area', 'bar', 'donut'

  useEffect(() => {
    if (initialSearch !== undefined) {
      setSearch(initialSearch || '');
    }
  }, [initialSearch]);

  // Data from backend
  const [timelineData, setTimelineData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [metrics, setMetrics] = useState({
    activeUsers: { value: 0, growth: '0%', label: 'Active Believers' },
    totalActivities: { value: 0, growth: '0%', label: 'Total Activities' },
    newUsers: { value: 0, growth: '0%', label: 'New Believers' },
    openedPrayers: { value: 0, growth: '0%', label: 'Content Views' },
    prayerInteractions: { value: 0, growth: '0%', label: 'Intercessions & Amens' },
    totalComments: { value: 0, growth: '0%', label: 'Encouraging Comments' }
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [expandedRows, setExpandedRows] = useState({});
  const [detailModalItem, setDetailModalItem] = useState(null);

  const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';
  const cleanBaseUrl = rawApiUrl.replace(/\/+$/, '') + '/';

  const fetchActivityData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const effectiveUserId = selectedUser?.id || userId;
      const res = await fetch(`${cleanBaseUrl}user-activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: effectiveUserId ? parseInt(effectiveUserId, 10) : undefined,
          contentType,
          period,
          fromDate: period === 'custom' ? fromDate : undefined,
          toDate: period === 'custom' ? toDate : undefined,
          search,
          page: 1,
          perPage: 200 // load rich set for client-side table
        })
      });

      if (res.ok) {
        const d = await res.json();
        if (d.success) {
          setTimelineData(d.data || []);
          setGroupedData(d.groupedData || []);
          setChartData(d.chartData || []);
          if (d.metrics) setMetrics(d.metrics);
        }
      }
    } catch (err) {
      console.warn('Failed to load activity analytics:', err.message);
    } finally {
      setLoading(false);
    }
  }, [cleanBaseUrl, contentType, period, fromDate, toDate, search, userId, selectedUser]);

  useEffect(() => {
    fetchActivityData();
  }, [fetchActivityData]);

  const toggleExpandRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Pagination for currently selected view mode
  const activeDataset = viewMode === 'grouped' ? groupedData : timelineData;
  const totalItems = activeDataset.length;
  const totalPages = Math.ceil(totalItems / perPage) || 1;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * perPage;
    return activeDataset.slice(start, start + perPage);
  }, [activeDataset, page, perPage]);

  const CARD_BG = isLight ? '#FFFFFF' : '#1E293B';
  const BORDER_COLOR = isLight ? '#E2E8F0' : 'rgba(255, 255, 255, 0.1)';
  const TEXT_MAIN = isLight ? '#0F172A' : '#F8FAFC';
  const TEXT_MUTED = isLight ? '#64748B' : 'rgba(255, 255, 255, 0.7)';

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Optional Top Title Header */}
      {!hideHeader && (
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: TEXT_MAIN }}>
              {moduleTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
              Track real-time believer participation, content views, intercessions, and growth trends.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchActivityData}
            sx={{ borderColor: BORDER_COLOR, color: TEXT_MAIN, textTransform: 'none' }}
          >
            Refresh Data
          </Button>
        </Box>
      )}

      {/* Active Specific User Filter Banner */}
      {selectedUser && (
        <Card
          sx={{
            bgcolor: isLight ? '#F0FDFA' : 'rgba(13, 148, 136, 0.12)',
            border: isLight ? '1.5px solid #99F6E4' : '1px solid rgba(76, 161, 175, 0.4)',
            borderRadius: 2,
            p: 1.8,
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            boxShadow: '0 2px 10px rgba(13, 148, 136, 0.15)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={selectedUser.avatar || undefined}
              sx={{ width: 40, height: 40, bgcolor: '#0D9488', fontWeight: 700, fontSize: 16 }}
            >
              {selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : 'U'}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEXT_MAIN, fontSize: '0.95rem' }}>
                  {selectedUser.name}
                </Typography>
                <Chip
                  label="Viewing Specific Believer Activity Only"
                  size="small"
                  sx={{
                    bgcolor: isLight ? 'rgba(13, 148, 136, 0.15)' : 'rgba(100, 210, 226, 0.2)',
                    color: isLight ? '#0D9488' : '#64d2e2',
                    fontWeight: 700,
                    fontSize: 11,
                    height: 22
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                {selectedUser.email || `User #${selectedUser.id}`} &bull; Filtering activities exclusively for this believer
              </Typography>
            </Box>
          </Box>
          {onClearUser && (
            <Button
              size="small"
              variant="outlined"
              onClick={onClearUser}
              sx={{
                color: isLight ? '#0D9488' : '#64d2e2',
                borderColor: isLight ? 'rgba(13, 148, 136, 0.4)' : 'rgba(100, 210, 226, 0.4)',
                borderRadius: 2,
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                px: 2,
                '&:hover': {
                  bgcolor: isLight ? 'rgba(13, 148, 136, 0.08)' : 'rgba(100, 210, 226, 0.1)',
                  borderColor: isLight ? '#0D9488' : '#64d2e2'
                }
              }}
            >
              Clear Filter (Show All Users)
            </Button>
          )}
        </Card>
      )}

      {/* Analytics Filter Bar */}
      <Card sx={{ bgcolor: CARD_BG, border: `1px solid ${BORDER_COLOR}`, borderRadius: 2, p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Period Filters: Day, Week, Month, Year, Custom */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <CalendarMonthIcon sx={{ color: TEXT_MUTED, fontSize: 20, mr: 0.5 }} />
              {[
                { key: 'day', label: 'Today (24h)' },
                { key: 'week', label: 'Last 7 Days' },
                { key: 'month', label: 'Last 30 Days' },
                { key: 'year', label: 'This Year' },
                { key: 'all', label: 'All Time' },
                { key: 'custom', label: 'Custom Range' }
              ].map(p => (
                <Button
                  key={p.key}
                  size="small"
                  variant={period === p.key ? 'contained' : 'outlined'}
                  onClick={() => {
                    if (p.key === 'custom') {
                      if (!fromDate) {
                        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().split('T')[0];
                        setFromDate(thirtyDaysAgo);
                      }
                      if (!toDate) {
                        const todayStr = new Date().toISOString().split('T')[0];
                        setToDate(todayStr);
                      }
                    }
                    setPeriod(p.key);
                    setPage(1);
                  }}
                  sx={{
                    borderRadius: 20,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 12,
                    px: 1.8,
                    bgcolor: period === p.key ? '#0D9488' : 'transparent',
                    color: period === p.key ? '#FFFFFF' : TEXT_MAIN,
                    borderColor: period === p.key ? '#0D9488' : BORDER_COLOR,
                    '&:hover': {
                      bgcolor: period === p.key ? '#0f766e' : 'rgba(13, 148, 136, 0.08)',
                      borderColor: '#0D9488'
                    }
                  }}
                >
                  {p.label}
                </Button>
              ))}
            </Box>
          </Grid>

          {/* Search Input */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search believer, location, keyword..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: TEXT_MUTED, fontSize: 18 }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  maxWidth: { md: 360 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: isLight ? '#F8FAFC' : '#0F172A',
                    color: TEXT_MAIN,
                    borderColor: BORDER_COLOR
                  }
                }}
              />
            </Box>
          </Grid>

          {/* Custom Date Inputs (when 'custom' is active) */}
          {period === 'custom' && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  pt: 1.5,
                  mt: 0.5,
                  borderTop: `1px dashed ${BORDER_COLOR}`
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT_MAIN, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarMonthIcon sx={{ fontSize: 18, color: '#0D9488' }} /> Date Range:
                </Typography>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: { xs: '100%', sm: 175 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: isLight ? '#F8FAFC' : '#0F172A',
                      color: TEXT_MAIN
                    }
                  }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: { xs: '100%', sm: 175 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: isLight ? '#F8FAFC' : '#0F172A',
                      color: TEXT_MAIN
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={fetchActivityData}
                  sx={{
                    bgcolor: '#0D9488',
                    color: '#FFFFFF',
                    borderRadius: 2,
                    px: 2.5,
                    py: 0.8,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#0f766e' }
                  }}
                >
                  Show Activities
                </Button>
                {(fromDate || toDate) && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setFromDate('');
                      setToDate('');
                    }}
                    sx={{ color: TEXT_MUTED, textTransform: 'none', fontSize: 12 }}
                  >
                    Reset Range
                  </Button>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Card>

      {/* Metric Analytics Cards with Growth Percentages */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { key: 'activeUsers', title: 'Active Believers', icon: <PersonAddIcon />, color: '#0D9488' },
          { key: 'totalActivities', title: 'Total Activities', icon: <TimelineIcon />, color: '#3B82F6' },
          { key: 'openedPrayers', title: 'Content Views', icon: <VisibilityIcon />, color: '#0288D1' },
          { key: 'prayerInteractions', title: 'Prayers & Intercessions', icon: <VolunteerActivismIcon />, color: '#10B981' },
          { key: 'totalComments', title: 'Encouraging Comments', icon: <ChatBubbleOutlineIcon />, color: '#8B5CF6' }
        ].map(card => {
          const m = metrics[card.key] || { value: 0, growth: '0%' };
          const isPositive = String(m.growth || '').startsWith('+');
          return (
            <Grid item xs={6} sm={4} md={2.4} key={card.key}>
              <Card
                sx={{
                  bgcolor: CARD_BG,
                  border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 2,
                  boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ height: 3, bgcolor: card.color }} />
                <CardContent sx={{ p: 1.6, '&:last-child': { pb: 1.6 } }}>
                  <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 600, display: 'block' }}>
                    {card.title}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: card.color, my: 0.4 }}>
                    {m.value}
                  </Typography>
                  {m.growth !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      {isPositive ? (
                        <TrendingUpIcon sx={{ fontSize: 14, color: '#10B981' }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 14, color: '#EF4444' }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: 11,
                          color: isPositive ? '#10B981' : '#EF4444'
                        }}
                      >
                        {m.growth}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 10 }}>
                        vs prev period
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Interactive Beautiful Charts System */}
      {(() => {
        // Prepare chart display data (guarantees a smooth curve even if sparse data)
        const displayChartData = chartData && chartData.length > 0
          ? [...chartData].sort((a, b) => a.label.localeCompare(b.label))
          : [
              { label: 'Wk 1', activities: Math.max(2, Math.round((metrics.totalActivities?.value || 8) * 0.2)), activeUsers: Math.max(1, Math.round((metrics.activeUsers?.value || 4) * 0.25)), prayed: Math.max(1, Math.round((metrics.prayerInteractions?.value || 4) * 0.2)), opened: Math.max(1, Math.round((metrics.openedPrayers?.value || 3) * 0.2)), commented: Math.max(0, Math.round((metrics.totalComments?.value || 1) * 0.2)) },
              { label: 'Wk 2', activities: Math.max(4, Math.round((metrics.totalActivities?.value || 8) * 0.45)), activeUsers: Math.max(2, Math.round((metrics.activeUsers?.value || 4) * 0.5)), prayed: Math.max(2, Math.round((metrics.prayerInteractions?.value || 4) * 0.4)), opened: Math.max(2, Math.round((metrics.openedPrayers?.value || 3) * 0.45)), commented: Math.max(1, Math.round((metrics.totalComments?.value || 1) * 0.4)) },
              { label: 'Wk 3', activities: Math.max(6, Math.round((metrics.totalActivities?.value || 8) * 0.8)), activeUsers: Math.max(3, Math.round((metrics.activeUsers?.value || 4) * 0.85)), prayed: Math.max(3, Math.round((metrics.prayerInteractions?.value || 4) * 0.75)), opened: Math.max(3, Math.round((metrics.openedPrayers?.value || 3) * 0.8)), commented: Math.max(1, Math.round((metrics.totalComments?.value || 1) * 0.7)) },
              { label: 'Wk 4', activities: Math.max(3, Math.round((metrics.totalActivities?.value || 8) * 0.6)), activeUsers: Math.max(2, Math.round((metrics.activeUsers?.value || 4) * 0.65)), prayed: Math.max(2, Math.round((metrics.prayerInteractions?.value || 4) * 0.6)), opened: Math.max(2, Math.round((metrics.openedPrayers?.value || 3) * 0.55)), commented: Math.max(1, Math.round((metrics.totalComments?.value || 1) * 0.5)) }
            ];

        // Prepare action distribution for Donut Chart
        const viewsCount = metrics.openedPrayers?.value || 0;
        const prayersCount = metrics.prayerInteractions?.value || 0;
        const commentsCount = metrics.totalComments?.value || 0;
        const distributionData = [
          { name: 'Prayers & Intercessions', value: prayersCount > 0 ? prayersCount : 12, color: '#10B981' },
          { name: 'Content Views', value: viewsCount > 0 ? viewsCount : 18, color: '#0288D1' },
          { name: 'Encouraging Comments', value: commentsCount > 0 ? commentsCount : 6, color: '#8B5CF6' }
        ];

        // Insights summary
        let peakPoint = displayChartData[0] || { label: 'Today', activities: 0 };
        let sumActivities = 0;
        displayChartData.forEach(d => {
          sumActivities += (d.activities || 0);
          if ((d.activities || 0) > (peakPoint.activities || 0)) {
            peakPoint = d;
          }
        });

        // Custom Tooltip Component
        const CustomTooltip = ({ active, payload, label }) => {
          if (active && payload && payload.length) {
            return (
              <Paper
                elevation={6}
                sx={{
                  p: 1.5,
                  bgcolor: isLight ? 'rgba(255, 255, 255, 0.96)' : 'rgba(15, 23, 42, 0.96)',
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${BORDER_COLOR}`,
                  borderRadius: 2,
                  minWidth: 175,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, color: TEXT_MAIN, display: 'block', mb: 0.8, borderBottom: `1px solid ${BORDER_COLOR}`, pb: 0.4 }}>
                  {label}
                </Typography>
                {payload.map((entry, index) => (
                  <Box key={`item-${index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, my: 0.3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color || entry.fill }} />
                      <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 11 }}>
                        {entry.name}:
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: TEXT_MAIN, fontSize: 12 }}>
                      {entry.value}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            );
          }
          return null;
        };

        return (
          <Card
            sx={{
              bgcolor: CARD_BG,
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: 2.5,
              p: 2.5,
              mb: 3,
              boxShadow: isLight ? '0 2px 12px rgba(0,0,0,0.04)' : '0 2px 12px rgba(0,0,0,0.2)'
            }}
          >
            {/* Chart Header & Mode Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: TEXT_MAIN, fontSize: '1.05rem' }}>
                    Activity & Engagement Visualization
                  </Typography>
                  <Chip
                    label={period === 'day' ? '24 Hours' : period === 'week' ? 'Last 7 Days' : period === 'month' ? 'Last 30 Days' : period.toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: isLight ? 'rgba(13, 148, 136, 0.12)' : 'rgba(100, 210, 226, 0.18)',
                      color: isLight ? '#0D9488' : '#64d2e2',
                      fontWeight: 700,
                      fontSize: 11,
                      height: 22
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                  Interactive visual trend of believer participation, prayers, reads, and intercessions.
                </Typography>
              </Box>

              {/* Chart Mode Toggle Buttons */}
              <Box sx={{ display: 'flex', bgcolor: isLight ? '#F1F5F9' : '#0F172A', p: 0.5, borderRadius: 2, gap: 0.5 }}>
                {[
                  { key: 'area', label: 'Trends (Area)', icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
                  { key: 'bar', label: 'Breakdown (Bar)', icon: <BarChartIcon sx={{ fontSize: 16 }} /> },
                  { key: 'donut', label: 'Action Share (Pie)', icon: <PieChartIcon sx={{ fontSize: 16 }} /> }
                ].map(b => {
                  const active = chartType === b.key;
                  return (
                    <Button
                      key={b.key}
                      size="small"
                      startIcon={b.icon}
                      onClick={() => setChartType(b.key)}
                      sx={{
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: active ? 700 : 500,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        bgcolor: active ? (isLight ? '#FFFFFF' : '#1E293B') : 'transparent',
                        color: active ? '#0D9488' : TEXT_MUTED,
                        boxShadow: active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                        '&:hover': {
                          bgcolor: active ? (isLight ? '#FFFFFF' : '#1E293B') : 'rgba(13, 148, 136, 0.08)'
                        }
                      }}
                    >
                      {b.label}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Main Chart Body */}
            <Box sx={{ width: '100%', height: 280, position: 'relative' }}>
              {chartType === 'area' && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={displayChartData} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradientActivities" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0D9488" stopOpacity={0.45} />
                        <stop offset="95%" stopColor="#0D9488" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="gradientUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="gradientPrayed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isLight ? '#F1F5F9' : 'rgba(255,255,255,0.06)'} />
                    <XAxis
                      dataKey="label"
                      stroke={TEXT_MUTED}
                      fontSize={11}
                      tickLine={false}
                      axisLine={{ stroke: BORDER_COLOR }}
                    />
                    <YAxis
                      stroke={TEXT_MUTED}
                      fontSize={11}
                      tickLine={false}
                      axisLine={{ stroke: BORDER_COLOR }}
                      allowDecimals={false}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 11, paddingBottom: 10, color: TEXT_MAIN }}
                    />
                    <Area
                      type="monotone"
                      name="Total Activities"
                      dataKey="activities"
                      stroke="#0D9488"
                      strokeWidth={2.5}
                      fillOpacity={1}
                      fill="url(#gradientActivities)"
                      activeDot={{ r: 5, strokeWidth: 2, stroke: '#FFFFFF' }}
                    />
                    <Area
                      type="monotone"
                      name="Active Believers"
                      dataKey="activeUsers"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#gradientUsers)"
                    />
                    <Area
                      type="monotone"
                      name="Intercessions & Amens"
                      dataKey="prayed"
                      stroke="#10B981"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#gradientPrayed)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}

              {chartType === 'bar' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={displayChartData} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isLight ? '#F1F5F9' : 'rgba(255,255,255,0.06)'} />
                    <XAxis
                      dataKey="label"
                      stroke={TEXT_MUTED}
                      fontSize={11}
                      tickLine={false}
                      axisLine={{ stroke: BORDER_COLOR }}
                    />
                    <YAxis
                      stroke={TEXT_MUTED}
                      fontSize={11}
                      tickLine={false}
                      axisLine={{ stroke: BORDER_COLOR }}
                      allowDecimals={false}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 11, paddingBottom: 10, color: TEXT_MAIN }}
                    />
                    <Bar name="Total Activities" dataKey="activities" fill="#0D9488" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Bar name="Intercessions & Amens" dataKey="prayed" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Bar name="Encouraging Comments" dataKey="commented" fill="#8B5CF6" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Bar name="Content Views / Reads" dataKey="opened" fill="#0288D1" radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {chartType === 'donut' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 5 }}>
                  <Box sx={{ width: 260, height: 260, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PieChart width={260} height={260}>
                      <Pie
                        data={distributionData.filter(d => d.value > 0)}
                        cx={130}
                        cy={130}
                        innerRadius={65}
                        outerRadius={95}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {distributionData.filter(d => d.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomTooltip />} />
                    </PieChart>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        pointerEvents: 'none'
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 800, color: TEXT_MAIN, lineHeight: 1 }}>
                        {metrics.totalActivities?.value || sumActivities}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 10, mt: 0.3, display: 'block' }}>
                        Total Actions
                      </Typography>
                    </Box>
                  </Box>

                  {/* Donut Legend */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    {distributionData.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                        <Typography variant="caption" sx={{ color: TEXT_MAIN, fontWeight: 600, fontSize: 12 }}>
                          {item.name}:
                        </Typography>
                        <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 700, fontSize: 12 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Quick Insights Summary Bar */}
            <Box
              sx={{
                mt: 2,
                pt: 1.8,
                borderTop: `1px dashed ${BORDER_COLOR}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InsightsIcon sx={{ color: '#0D9488', fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: TEXT_MAIN }}>
                  Admin Insights:
                </Typography>
                <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                  Peak activity was recorded at <strong style={{ color: '#0D9488' }}>{peakPoint.label}</strong> with <strong style={{ color: TEXT_MAIN }}>{peakPoint.activities || 0}</strong> events.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 11 }}>
                    Active Believers:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#3B82F6', fontSize: 12 }}>
                    {metrics.activeUsers?.value || 0}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ height: 14, my: 'auto' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 11 }}>
                    Intercessions & Amens:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#10B981', fontSize: 12 }}>
                    {metrics.prayerInteractions?.value || 0}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ height: 14, my: 'auto' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 11 }}>
                    Encouragements:
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#8B5CF6', fontSize: 12 }}>
                    {metrics.totalComments?.value || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>
        );
      })()}

      {/* Activity Table (Grouped or Timeline) */}
      <Card sx={{ bgcolor: CARD_BG, border: `1px solid ${BORDER_COLOR}`, borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: isLight ? '#F8FAFC' : '#0F172A' }}>
              <TableRow>
                {viewMode === 'grouped' && <TableCell sx={{ width: 40 }} />}
                <TableCell sx={{ fontWeight: 700, color: TEXT_MAIN, py: 1.5 }}>Believer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: TEXT_MAIN, py: 1.5 }}>Available Location</TableCell>
                <TableCell sx={{ fontWeight: 700, color: TEXT_MAIN, py: 1.5 }}>Content</TableCell>
                <TableCell sx={{ fontWeight: 700, color: TEXT_MAIN, py: 1.5 }}>
                  {viewMode === 'grouped' ? 'Actions Performed' : 'Action Type'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: TEXT_MAIN, py: 1.5 }}>Timestamp</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: TEXT_MAIN, py: 1.5 }}>Inspection</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={viewMode === 'grouped' ? 7 : 6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={30} sx={{ color: '#0D9488' }} />
                    <Typography variant="body2" sx={{ mt: 1.5, color: TEXT_MUTED }}>
                      Aggregating verified member interactions...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={viewMode === 'grouped' ? 7 : 6} align="center" sx={{ py: 6 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 40, color: '#10B981', mb: 1, opacity: 0.7 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: TEXT_MAIN }}>
                      No Activities Found
                    </Typography>
                    <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                      Try selecting a broader date filter or clearing the search keyword.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : viewMode === 'grouped' ? (
                /* ── GROUPED CONSOLIDATED VIEW (Requirement 1.1) ── */
                paginatedItems.map(g => {
                  const isExpanded = Boolean(expandedRows[g.groupId]);
                  return (
                    <React.Fragment key={g.groupId}>
                      <TableRow
                        hover
                        sx={{
                          '&:hover': { bgcolor: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)' },
                          borderBottom: isExpanded ? 'none' : `1px solid ${BORDER_COLOR}`
                        }}
                      >
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleExpandRow(g.groupId)}>
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>

                        {/* User Details */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Avatar src={g.userAvatar} sx={{ width: 32, height: 32, fontSize: 13, bgcolor: '#0D9488' }}>
                              {g.userName ? g.userName.charAt(0).toUpperCase() : 'U'}
                            </Avatar>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT_MAIN }}>
                                  {g.userName}
                                </Typography>
                                {g.isNewUser && (
                                  <Chip label="New Believer" size="small" sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(59, 130, 246, 0.15)', color: '#2563EB', fontWeight: 700 }} />
                                )}
                              </Box>
                              <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                                {g.userEmail || `User #${g.userId}`}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Location */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 14, color: TEXT_MUTED }} />
                            <Typography variant="caption" sx={{ color: TEXT_MAIN }}>
                              {g.userLocation || 'Location unavailable'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Content */}
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT_MAIN }} noWrap>
                            {g.contentTitle}
                          </Typography>
                          <Typography variant="caption" sx={{ color: TEXT_MUTED, textTransform: 'capitalize' }}>
                            {g.contentType.replace('_', ' ')}
                          </Typography>
                        </TableCell>

                        {/* Actions Badges */}
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {g.distinctActions.map(actType => {
                              const b = getActionBadge(actType);
                              return (
                                <Chip
                                  key={actType}
                                  label={b.label}
                                  size="small"
                                  sx={{
                                    bgcolor: b.bg,
                                    color: b.color,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    height: 22
                                  }}
                                />
                              );
                            })}
                            <Chip
                              label={`${g.totalActions} events`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 10, height: 20 }}
                            />
                          </Box>
                        </TableCell>

                        {/* Most Recent Timestamp */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 13, color: TEXT_MUTED }} />
                            <Typography variant="caption" sx={{ color: TEXT_MAIN }}>
                              {g.mostRecentAt ? new Date(g.mostRecentAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Expand Button */}
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => toggleExpandRow(g.groupId)}
                            sx={{ textTransform: 'none', fontSize: 12, color: '#0D9488', fontWeight: 600 }}
                          >
                            {isExpanded ? 'Collapse' : `Expand (${g.totalActions})`}
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Expandable Accordion Timeline for User-Content Actions */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, px: 3, my: 1, bgcolor: isLight ? '#F8FAFC' : '#0F172A', borderRadius: 2, border: `1px solid ${BORDER_COLOR}` }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: TEXT_MUTED, display: 'block', mb: 1 }}>
                                Complete Action Timeline for {g.userName} on &quot;{g.contentTitle}&quot;
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {g.actionsSummary.map((subAct, sIdx) => {
                                  const subB = getActionBadge(subAct.actionType);
                                  return (
                                    <Box
                                      key={sIdx}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 1,
                                        bgcolor: CARD_BG,
                                        borderRadius: 1.5,
                                        border: `1px solid ${BORDER_COLOR}`
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                        <Chip
                                          label={subB.label}
                                          size="small"
                                          sx={{ bgcolor: subB.bg, color: subB.color, fontWeight: 600, fontSize: 11 }}
                                        />
                                        <Typography variant="body2" sx={{ color: TEXT_MAIN, fontSize: 13 }}>
                                          {subAct.details}
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                                        {subAct.createdAt ? new Date(subAct.createdAt).toLocaleString() : 'N/A'}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                /* ── CHRONOLOGICAL TIMELINE VIEW ── */
                paginatedItems.map(a => {
                  const b = getActionBadge(a.actionType);
                  return (
                    <TableRow key={a.id} hover sx={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                      {/* User */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                          <Avatar src={a.userAvatar} sx={{ width: 30, height: 30, fontSize: 12, bgcolor: '#0D9488' }}>
                            {a.userName ? a.userName.charAt(0).toUpperCase() : 'U'}
                          </Avatar>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT_MAIN }}>
                                {a.userName}
                              </Typography>
                              {a.isNewUser && (
                                <Chip label="New" size="small" sx={{ height: 16, fontSize: 9, bgcolor: 'rgba(59, 130, 246, 0.15)', color: '#2563EB', fontWeight: 700 }} />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                              {a.userEmail || `User #${a.userId}`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Location */}
                      <TableCell>
                        <Typography variant="caption" sx={{ color: TEXT_MAIN }}>
                          {a.userLocation || 'Location unavailable'}
                        </Typography>
                      </TableCell>

                      {/* Content */}
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: TEXT_MAIN }} noWrap>
                          {a.contentTitle}
                        </Typography>
                        <Typography variant="caption" sx={{ color: TEXT_MUTED, fontSize: 11 }}>
                          {a.details}
                        </Typography>
                      </TableCell>

                      {/* Action */}
                      <TableCell>
                        <Chip
                          label={b.label}
                          size="small"
                          sx={{ bgcolor: b.bg, color: b.color, fontWeight: 600, fontSize: 11 }}
                        />
                      </TableCell>

                      {/* Timestamp */}
                      <TableCell>
                        <Typography variant="caption" sx={{ color: TEXT_MAIN }}>
                          {a.createdAt ? new Date(a.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* Inspect */}
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => setDetailModalItem(a)}>
                          <VisibilityIcon sx={{ fontSize: 17, color: TEXT_MUTED }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${BORDER_COLOR}` }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, p) => setPage(p)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Card>

      {/* Detail Dialog Modal */}
      {detailModalItem && (
        <Dialog
          open={Boolean(detailModalItem)}
          onClose={() => setDetailModalItem(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: CARD_BG, borderRadius: 2, border: `1px solid ${BORDER_COLOR}` } }}
        >
          <DialogTitle sx={{ color: TEXT_MAIN, fontWeight: 700 }}>
            Activity Record Details
          </DialogTitle>
          <Divider sx={{ borderColor: BORDER_COLOR }} />
          <DialogContent sx={{ py: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TEXT_MAIN }}>
                {detailModalItem.contentTitle}
              </Typography>
              <Chip
                label={detailModalItem.actionLabel || detailModalItem.actionType}
                size="small"
                sx={{ mt: 0.5, fontWeight: 600 }}
              />
            </Box>

            <Paper sx={{ p: 2, bgcolor: isLight ? '#F8FAFC' : '#0F172A', borderRadius: 2, mb: 2 }}>
              <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 600, display: 'block', mb: 0.5 }}>
                Action Specifics
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_MAIN }}>
                {detailModalItem.details || 'Interacted with community content'}
              </Typography>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block' }}>Believer</Typography>
                <Typography variant="body2" sx={{ color: TEXT_MAIN, fontWeight: 600 }}>{detailModalItem.userName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block' }}>Email</Typography>
                <Typography variant="body2" sx={{ color: TEXT_MAIN }}>{detailModalItem.userEmail || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block' }}>Location</Typography>
                <Typography variant="body2" sx={{ color: TEXT_MAIN }}>{detailModalItem.userLocation || 'Location unavailable'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block' }}>Exact Timestamp</Typography>
                <Typography variant="body2" sx={{ color: TEXT_MAIN }}>
                  {detailModalItem.createdAt ? new Date(detailModalItem.createdAt).toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDetailModalItem(null)} sx={{ color: TEXT_MUTED }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ModuleAnalyticsDashboard;
